import { useCallback, useEffect, useState } from "react";
import { PATCH_NOTES } from "../data/patch-notes";
import type { PatchNote } from "../types";

const STORAGE_KEY = "patch-notes-last-seen";
const MAX_SHOWN = 3;

const readLastSeen = (): number | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  // String vazia/whitespace viraria Number(...) === 0 e passaria no isFinite.
  if (raw === null || raw.trim() === "") return null;
  const parsed = Number(raw);
  // Valor corrompido é tratado como navegador novo (fail-soft, nunca lança).
  return Number.isFinite(parsed) ? parsed : null;
};

const getUnseenNotes = (): PatchNote[] => {
  const valid = PATCH_NOTES.filter(
    (note) =>
      Number.isFinite(note.version) &&
      (note.news?.length || note.improvements?.length || note.fixes?.length),
  ).sort((a, b) => b.version - a.version);
  if (valid.length === 0) return [];

  const lastSeen = readLastSeen();
  // Navegador novo/limpo: só a nota mais recente, nunca o histórico.
  if (lastSeen === null) return valid.slice(0, 1);
  return valid.filter((note) => note.version > lastSeen);
};

export const usePatchNotes = () => {
  // Inicializador lazy: leitura pura, idempotente sob o StrictMode (2x em dev).
  const [unseen] = useState<PatchNote[]>(getUnseenNotes);
  const [open, setOpen] = useState(unseen.length > 0);

  useEffect(() => {
    if (unseen.length === 0) return;
    const lastSeen = readLastSeen() ?? -1;
    // Idempotente: a 2ª execução do StrictMode grava o mesmo valor.
    localStorage.setItem(
      STORAGE_KEY,
      String(Math.max(lastSeen, unseen[0].version)),
    );
  }, [unseen]);

  const close = useCallback(() => setOpen(false), []);

  return {
    open,
    notes: unseen.slice(0, MAX_SHOWN),
    hasMore: unseen.length > MAX_SHOWN,
    close,
  };
};
